<template>
  <div class="org-manager">
    <template v-if="!showingChild">
      <span class="note"
        >Search, add, update or delete organizations below.</span
      >
      <div class="toolbar">
        <div class="searchbar">
          <input
            style="width: 100%"
            v-model="search"
            type="text"
            placeholder="organization name"
            @keyup.enter="doSearch"
            autofocus
          />
          <button class="icon-button" @click="doSearch">
            <span class="material-icons">search</span>
          </button>
          <button
            v-if="search.trim().length"
            class="icon-button"
            @click="addOrganization"
          >
            <span class="material-icons">add</span>
          </button>
        </div>
        <div v-if="organizations.length > 0" class="sort-controls">
          <span class="sort-label">Sort:</span>
          <button
            class="sort-button"
            :class="{ active: sortBy === 'name' }"
            @click="setSortBy('name')"
          >
            Name
            <span v-if="sortBy === 'name'" class="material-icons sort-icon">
              {{ sortOrder === "asc" ? "arrow_upward" : "arrow_downward" }}
            </span>
          </button>
          <button
            class="sort-button"
            :class="{ active: sortBy === 'users' }"
            @click="setSortBy('users')"
          >
            User Count
            <span v-if="sortBy === 'users'" class="material-icons sort-icon">
              {{ sortOrder === "asc" ? "arrow_upward" : "arrow_downward" }}
            </span>
          </button>
        </div>
      </div>
      <span
        v-if="errorMessage"
        class="details error"
        style="margin-bottom: 1rem"
      >
        {{ errorMessage }}
      </span>
      <div v-if="loading" class="spinner-container">
        <div class="spinner"></div>
      </div>
      <ul v-if="sortedOrganizations.length">
        <li v-for="org in sortedOrganizations" :key="org.id" class="org-item">
          <template v-if="editId === org.id">
            <input v-model="editName" class="edit-input" autofocus />
            <label class="verified-toggle">
              <input type="checkbox" v-model="editVerified" />
              <span>Verified</span>
            </label>
            <div style="display: flex; column-gap: 1rem">
              <button class="icon-button save" @click="saveEdit(org.id)">
                <span class="material-icons">save</span>
              </button>
              <button class="icon-button cancel" @click="cancelEdit">
                <span class="material-icons">close</span>
              </button>
            </div>
          </template>
          <template v-else>
            <div class="org-info">
              <span @click="goToOrg(org)" style="cursor: pointer">
                {{ org.name }}
                <span
                  v-if="org.verified"
                  class="verified-badge"
                  title="Verified"
                >
                  <span class="material-icons verified-icon">verified</span>
                </span>
              </span>
              <span class="user-count">{{ org.user_count || 0 }} users</span>
            </div>
            <div style="display: flex; column-gap: 1rem">
              <button class="icon-button edit-btn" @click="startEdit(org)">
                <span class="material-icons">edit</span>
              </button>
              <button
                class="icon-button delete-btn"
                @click="deleteOrganization(org.id)"
              >
                <span class="material-icons">delete</span>
              </button>
            </div>
          </template>
        </li>
      </ul>
      <div
        v-else-if="
          search.length && !loading && organizations.length === 0 && hasSearched
        "
      >
        No organizations found.
      </div>
    </template>
    <router-view />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useAdminApi } from "@/plugins/api";
import { useRouter, useRoute } from "vue-router";
const router = useRouter();
const route = useRoute();
const $adminApi = useAdminApi();

const showingChild = computed(() => !!route.params.organizationName);

const search = ref("");
const organizations = ref([]);
const errorMessage = ref(null);
const hasSearched = ref(false);
const loading = ref(false);
const sortOrder = ref("asc");
const sortBy = ref("name");

const editId = ref(null);
const editName = ref("");
const editVerified = ref(false);

//refresh values if user navigates back from child view
watch(showingChild, (isShowingChild, wasShowingChild) => {
  if (wasShowingChild && !isShowingChild && hasSearched.value) {
    doSearch();
  }
});

const sortedOrganizations = computed(() => {
  return organizations.value.slice().sort((a, b) => {
    let comparison = 0;

    if (sortBy.value === "name") {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy.value === "users") {
      comparison = (a.user_count || 0) - (b.user_count || 0);
    }

    return sortOrder.value === "asc" ? comparison : -comparison;
  });
});

function goToOrg(org) {
  router.push({
    name: "Organization Details",
    params: { organizationName: org.name },
    query: { organizationId: org.id },
  });
}

async function doSearch() {
  errorMessage.value = null;
  loading.value = true;
  hasSearched.value = true;
  try {
    organizations.value = await $adminApi.getOrganizations(search.value.trim());
  } catch (error) {
    errorMessage.value = "Error fetching organizations.";
    organizations.value = [];
    console.error(error);
  } finally {
    loading.value = false;
  }
}

async function addOrganization() {
  errorMessage.value = null;
  loading.value = true;
  try {
    await $adminApi.postOrganization(search.value.trim());
    await doSearch();
  } catch (error) {
    if (error.status === 409) {
      errorMessage.value = "Organization already exists.";
    } else {
      errorMessage.value = "Error adding organization.";
    }
  } finally {
    loading.value = false;
  }
}

async function deleteOrganization(id) {
  errorMessage.value = null;
  loading.value = true;
  try {
    await $adminApi.deleteOrganization(id);
    organizations.value = organizations.value.filter((org) => org.id !== id);
  } catch (error) {
    errorMessage.value = "Error deleting organization.";
    console.error(error);
  } finally {
    loading.value = false;
  }
}

function startEdit(org) {
  editId.value = org.id;
  editName.value = org.name;
  editVerified.value = !!org.verified;
}

function cancelEdit() {
  editId.value = null;
  editName.value = "";
  editVerified.value = false;
}

async function saveEdit(id) {
  errorMessage.value = null;
  loading.value = true;
  try {
    await $adminApi.putOrganization(id, {
      name: editName.value.trim(),
      verified: editVerified.value,
    });

    const orgIndex = organizations.value.findIndex((org) => org.id === id);
    if (orgIndex !== -1) {
      organizations.value[orgIndex].name = editName.value.trim();
      organizations.value[orgIndex].verified = editVerified.value;
    }
    cancelEdit();
  } catch (error) {
    errorMessage.value = "Error updating organization.";
    console.error(error);
  } finally {
    loading.value = false;
  }
}

function setSortBy(field) {
  if (sortBy.value === field) {
    // Toggle order if clicking same field
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    // Set new field and default to ascending
    sortBy.value = field;
    sortOrder.value = "asc";
  }
}
</script>

<style scoped>
.org-manager {
  padding: 1rem;
}
.toolbar {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
.searchbar {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.sort-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: var(--bg2);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.sort-label {
  font-size: 0.875rem;
  color: var(--fg2);
  font-weight: 500;
}
.sort-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: transparent;
  color: var(--fg2);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.sort-button:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.3);
}
.sort-button.active {
  background: rgba(25, 118, 210, 0.2);
  border-color: var(--bg3);
}
.sort-icon {
  font-size: 1rem;
  margin-left: 0.25rem;
}
.org-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}
.org-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.user-count {
  font-size: 0.875rem;
  color: #757575;
}
.note {
  font-style: italic;
  font-size: smaller;
}
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  margin-bottom: 1rem;
}
.edit-input {
  min-width: 120px;
  margin-right: 1rem;
}
.icon-button {
  width: 4rem !important;
  height: 2.5rem !important;
  min-width: 2rem !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sort-btn {
  width: 2.5rem !important;
  height: 2.5rem !important;
  background-color: transparent !important;
  color: var(--fg1) !important;
}
.verified-badge {
  color: #3aae49;
  vertical-align: middle;
}
.verified-icon {
  font-size: 1.2rem;
}
.verified-toggle {
  display: flex;
  align-items: center;
  margin-right: 1rem;
  gap: 0.3rem;
}

ul {
  list-style-type: none;
  padding: 0;
}
.cancel-btn {
  color: #757575;
}
.save {
  color: #2d7a31;
}
.edit-btn {
  color: black;
}
.delete-btn {
  color: #d32f2f;
}
</style>
